import React, { useEffect, useState } from 'react';
import { Gift, X, ArrowRight } from 'lucide-react';
import { useUIStore } from '../../stores/useUIStore';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';

export const RaffleModal: React.FC = () => {
  const isOpen = useUIStore((s) => s.isRaffleModalOpen);
  const closeStore = useUIStore((s) => s.closeRaffleModal);
  const navigateTo = useUIStore((s) => s.navigateTo);

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      lockScroll();
      const timer = setTimeout(() => setVisible(true), 30);
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
      const timer = setTimeout(() => {
        setMounted(false);
        unlockScroll();
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      closeStore();
    }, 320);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mounted) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mounted]);

  if (!mounted) return null;

  const handleParticipate = () => {
    handleClose();
    navigateTo('sorteos', '/sorteos');
  };

  return (
    <div
      className={`fixed inset-0 z-[200000] soft-backdrop flex items-center justify-center p-4 overscroll-contain select-none transition-opacity duration-300 ease-out ${
        visible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="raffle-modal-title"
    >
      <div
        className={`w-full max-w-[420px] bg-white text-[#06173f] p-7 sm:p-8 text-center border-2 border-[var(--line)] shadow-2xl relative transition-all duration-300 ease-out select-none ${
          visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
        }`}
        style={{
          clipPath:
            'polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px)',
          filter: 'drop-shadow(8px 8px 0 rgba(6,23,63,0.25))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Botón Cerrar */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-[var(--ice)] hover:bg-[#e2e8f0] text-[var(--soft)] hover:text-[#06173f] flex items-center justify-center transition-colors cursor-pointer"
          title="Cerrar"
          aria-label="Cerrar modal"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Icono de Regalo Central */}
        <div
          className="w-16 h-16 mx-auto mb-4 bg-[rgba(20,71,230,0.08)] text-[#1447e6] border-2 border-[rgba(20,71,230,0.18)] flex items-center justify-center text-2xl"
          style={{
            clipPath:
              'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)',
          }}
        >
          <Gift className="w-8 h-8 text-[#1447e6]" />
        </div>

        {/* Textos */}
        <h2
          id="raffle-modal-title"
          className="text-xl sm:text-2xl font-black uppercase tracking-tight text-[#06173f] font-heading leading-tight"
        >
          ¡Sorteo Semanal de Pavos!
        </h2>

        <div className="text-base font-black text-[#1447e6] font-heading mt-1 mb-3">
          Premio: 800 Pavos en Saldo
        </div>

        <p className="text-xs text-[var(--soft)] leading-relaxed mb-6 font-medium">
          ¡Participa gratis cada semana, sin necesidad de realizar ninguna compra!
          <br />
          Todos los domingos se realizan los sorteos.
          <br />
          ¡Mucha suerte!
        </p>

        {/* Acciones */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={handleParticipate}
            className="w-full bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black py-3.5 uppercase tracking-wider text-xs transition flex items-center justify-center gap-2 shadow-lg cursor-pointer"
            style={{
              clipPath:
                'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
              color: '#ffffff',
            }}
          >
            <span>Participar en el Sorteo</span>
            <ArrowRight className="w-4 h-4 text-white" />
          </button>

          <button
            type="button"
            onClick={handleClose}
            className="w-full py-2 text-xs font-semibold text-[var(--soft)] hover:text-[#06173f] transition cursor-pointer"
          >
            Quizás más tarde
          </button>
        </div>
      </div>
    </div>
  );
};

export default RaffleModal;
